// @ts-strict-ignore
import { createSelector } from 'reselect';
import { RootState } from '@/reducers/reducers';
import {
    OrgSummaryPreferences,
    SummaryViewResponseAPI,
    OrgDestinationsResponse,
    SummaryViewPageCacheEntry,
} from '@/types/summaryView';
import { processDestinations, processVersions } from '@/helpers/summaryViewHelpers';
import { Destination } from '@/types/destinations';

// Org-level selectors
export const selectDestinationsData = (state: RootState, orgId: string): OrgDestinationsResponse | undefined => {
    if (state.ui?.uiConfig?.demoData?.orgDestinations?.[orgId]) {
        return state.ui?.uiConfig?.demoData?.orgDestinations?.[orgId];
    }

    return state.entities.summaryView.orgDestinations?.[orgId];
};

export const selectCanAccessTabularView = (state: RootState, orgId: string): boolean =>
    selectDestinationsData(state, orgId)?.canAccessTabularView ?? false;

export const selectHasAnyWorkflowsApp = (state: RootState, orgId: string): boolean =>
    selectDestinationsData(state, orgId)?.appDestinations?.some((app) => app.renderWorkflowsOverview) ?? false;

export const selectTabularViewDestinationLimit = (state: RootState, orgId: string): number | undefined =>
    selectDestinationsData(state, orgId)?.tabularViewDestinationLimit;

// App-level selectors
export const selectShouldRenderWorkflowsOverviewForApp = (state: RootState, orgId: string, appId: string): boolean => {
    if (state.ui?.uiConfig?.demoData?.appsDemoData?.[appId]) {
        return !!state.ui.uiConfig.demoData.appsDemoData[appId].app.workflowOverview;
    }

    const orgDestinations = selectDestinationsData(state, orgId);
    return orgDestinations?.appDestinations?.find((app) => app.appId === appId)?.renderWorkflowsOverview ?? false;
};

export const selectIsDestinationsDataLoadedForApp = (state: RootState, orgId: string, appId: string): boolean => {
    if (state.ui?.uiConfig?.demoData?.appsDemoData?.[appId]) {
        return true;
    }
    return !!selectDestinationsData(state, orgId);
};

// Shared selectors (work for org or app level)
export const selectAvailableDestinations = createSelector(
    [selectDestinationsData, (_state: RootState, _orgId: string, appId?: string) => appId],
    (orgData, appId): Destination[] => processDestinations(orgData, appId),
);

export const selectAvailableDestinationIds = createSelector([selectAvailableDestinations], (destinations): string[] =>
    destinations.map((d) => d.stepConfigId),
);

export const selectAvailableVersions = createSelector(
    [selectDestinationsData, (_state: RootState, _orgId: string, appId?: string) => appId],
    (orgData, appId): string[] => processVersions(orgData, appId),
);

export const selectActiveVersion = createSelector(
    [selectDestinationsData, (_state: RootState, _orgId: string, appId?: string) => appId],
    (orgData, appId): string | null => {
        if (!orgData?.appDestinations) return null;
        const relevantApps =
            appId ? orgData.appDestinations.filter((app) => app.appId === appId) : orgData.appDestinations;
        return relevantApps[0]?.activeVersion ?? null;
    },
);

export const selectLiveVersion = createSelector(
    [selectDestinationsData, (_state: RootState, _orgId: string, appId?: string) => appId],
    (orgData, appId): string | null => {
        if (!orgData?.appDestinations) return null;
        const relevantApps =
            appId ? orgData.appDestinations.filter((app) => app.appId === appId) : orgData.appDestinations;
        return relevantApps[0]?.liveVersion ?? null;
    },
);

// Generic selectors (use requestId)
export const createSummaryRequestId = (orgId: string, appId?: string): string =>
    appId ? `org-summary-${orgId}-${appId}` : `org-summary-${orgId}`;

export const selectSummaryPreferencesState = (state: RootState) => state.orgSummaryPreferences;

export const selectSummaryPreferencesForApp = (state: RootState, appId?: string): OrgSummaryPreferences | undefined => {
    if (appId && state.ui?.uiConfig?.demoData?.summaryPreferences?.[appId]) {
        return state.ui.uiConfig.demoData.summaryPreferences[appId];
    }

    return appId ? state.orgSummaryPreferences.byAppId[appId] : undefined;
};

export const selectSummaryViewByRequestId = (
    state: RootState,
    requestId: string,
): SummaryViewResponseAPI | undefined => {
    return state.ui?.uiConfig?.demoData?.summaryViewByRequestId?.[requestId];
};

export const selectSummaryViewEntry = (
    state: RootState,
    requestId: string,
    rowSetSignature: string,
): SummaryViewPageCacheEntry | undefined => {
    return state.entities.summaryView.byId?.[requestId]?.entriesByRowSetSignature?.[rowSetSignature];
};

// The most recently fetched query for this view, whatever its filters — used for the single-page
// reuse path, which re-filters a cached page locally instead of refetching.
export const selectLatestSummaryViewEntry = (
    state: RootState,
    requestId: string,
): SummaryViewPageCacheEntry | undefined => {
    const cache = state.entities.summaryView.byId?.[requestId];
    const latestSignature = cache?.recentRowSetSignatures?.[0];
    return latestSignature ? cache.entriesByRowSetSignature[latestSignature] : undefined;
};
